if (!customElements.get("product-form")) {
  // Add to bag under load: the add's own timeout, and the longest Retry-After a 429 is retried after (once).
  const HB_ADD_TIMEOUT_MS = 10000;
  const HB_ADD_RETRY_MAX_WAIT_MS = 3000;
  const HB_ADD_RETRY_DEFAULT_WAIT_MS = 1000;

  // Retry-After in seconds or as an HTTP date; a missing or unreadable value waits HB_ADD_RETRY_DEFAULT_WAIT_MS.
  const parseRetryAfterMs = (value) => {
    if (value == null || value === "") return HB_ADD_RETRY_DEFAULT_WAIT_MS;
    const seconds = Number(value);
    if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
    const at = Date.parse(value);
    return Number.isFinite(at) ? Math.max(0, at - Date.now()) : HB_ADD_RETRY_DEFAULT_WAIT_MS;
  };

  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.submitLockActive = false;
        this.hasGlobalLockListener = false;
        this.onGlobalAtcLockChange = this.onGlobalAtcLockChange.bind(this);

        this.form = this.querySelector("form");
        if (this.form) {
          this.handleFormSubmit();
        } else {
          this.createFormElement();
        }
      }

      /*
       * Page adds use the notification when the header renders one; without it (settings.cart_drawer_open_on_add)
       * they open the drawer. Forms inside the drawer always render back into it. Resolve on every read because
       * the drawer can upgrade after this form was built.
       */
      get cart() {
        const drawer = document.querySelector("cart-drawer");
        return this.closest("#CartDrawer") ? drawer : document.querySelector("cart-notification") || drawer;
      }

      handleFormSubmit() {
        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector("span");
        this.refreshSubmitButtonText();
        this.storeSubmitButtonDefaultLabel();

        if (!this.hasGlobalLockListener) {
          document.addEventListener("hb:atc-lock-change", this.onGlobalAtcLockChange);
          this.hasGlobalLockListener = true;
        }
        this.onGlobalAtcLockChange();

        // Page adds open a dialog (the notification or the drawer); drawer cards stay put.
        if (this.cart && !this.closest("#CartDrawer")) {
          this.submitButton?.setAttribute("aria-haspopup", "dialog");
        }

        this.hideErrors = this.dataset.hideErrors === "true";

        // bind once
        this._boundSubmit = this.onSubmitHandler.bind(this);
        this.form.addEventListener("submit", this._boundSubmit, { passive: false });
      }

      createFormElement() {
        const productForm = this;
        const formId = productForm.dataset.formId;

        if (productForm) {
          const errorWrapper = document.querySelector(`.product-form__error-message-wrapper[data-form-id="${formId}"]`);

          const form = document.createElement("form");
          form.setAttribute("method", "post");
          form.setAttribute("action", "/cart/add");
          form.setAttribute("id", productForm.dataset.formId);
          form.setAttribute("accept-charset", "UTF-8");
          form.setAttribute("class", "form");
          form.setAttribute("enctype", "multipart/form-data");
          form.setAttribute("novalidate", "novalidate");
          form.setAttribute("data-type", "add-to-cart-form");

          if (errorWrapper) {
            const errorParent = errorWrapper.parentElement;
            errorParent.removeChild(errorWrapper);
          }

          while (productForm.firstChild) form.appendChild(productForm.firstChild);

          if (errorWrapper) {
            const errorParent = errorWrapper.parentElement;
            errorParent.appendChild(errorWrapper);
          }

          productForm.appendChild(form);

          this.form = this.querySelector("form");
          if (this.form) {
            this.handleFormSubmit();
          }
        }
      }

      // util fetch with timeout (assets/global.js hbCartFetchWithTimeout; the fallback covers a page without it)
      async fetchWithTimeout(input, init, ms = 8000) {
        if (typeof window.hbCartFetchWithTimeout === "function") return window.hbCartFetchWithTimeout(input, init, ms);
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), ms);
        try {
          const res = await fetch(input, { ...init, signal: controller.signal });
          clearTimeout(id);
          return res;
        } catch (e) {
          clearTimeout(id);
          throw e;
        }
      }

      async fetchCart() {
        try {
          const cartJsonUrl = `${routes.cart_url}.js`;
          const res = await this.fetchWithTimeout(cartJsonUrl, { headers: { Accept: "application/json" } }, 6000);
          // A 429 answers with an HTML page, which res.json() failed on and logged as an error. The add goes
          // on without a snapshot either way (the server still guards quantities). The body decides, not
          // the content type, which Shopify does not always send as JSON for the cart.
          const cart = res.ok ? await res.json().catch(() => null) : null;
          if (!cart) console.warn(`Cart snapshot skipped: HTTP ${res.status}`);
          return cart;
        } catch (err) {
          console.error("Error fetching cart:", err);
          return null;
        }
      }

      validateMaxQuantityWithCartSnapshot(formData, formElement, cart) {
        let errorMessage = "";
        const quantityToAdd = Number(formData.get("quantity")) || 1;

        const maxQuantityInput =
          formElement.querySelector('input[name="max-quantity"]') ||
          formElement.querySelector('input[name="max-quantity--quickview"]');
        const maxQuantity = maxQuantityInput && maxQuantityInput.value != "" ? Number(maxQuantityInput.value) : null;

        const invQuantityInput =
          formElement.querySelector('input[name="inventory-quantity"]') ||
          formElement.querySelector('input[name="inventory-quantity--quickview"]');
        const invQuantity = invQuantityInput ? Number(invQuantityInput.value) : null;

        const variantIdInput = formElement.querySelector('input[name="id"]');
        const variantId = variantIdInput ? variantIdInput.value : null;

        if (!variantId || maxQuantity === null || isNaN(maxQuantity) || !cart) return false;

        const cartItem = cart?.items?.find((item) => item?.variant_id == variantId);
        const currentQuantityInCart = cartItem ? cartItem?.quantity : 0;

        if (invQuantity != null && currentQuantityInCart >= invQuantity) {
          errorMessage = window.cartStrings.outOfStockError;
          this.handleErrorMessage(errorMessage);
          return true;
        }

        if (currentQuantityInCart >= maxQuantity) {
          errorMessage = window.cartStrings.maxInCartError;
          this.handleErrorMessage(errorMessage);
          return true;
        }

        if (quantityToAdd + currentQuantityInCart > maxQuantity) {
          const availableToAdd = maxQuantity - currentQuantityInCart;
          errorMessage = window.cartStrings.limitExceededError.replace("[availableToAdd]", availableToAdd);
          this.handleErrorMessage(errorMessage);
          return true;
        }

        return false;
      }

      validateHazmatRules(formData, formElement, cart) {
        const thresholdInput = formElement.querySelector('input[name="hazmat-country-threshold"]');
        if (!thresholdInput) return false; // Country not listed => no restrictions

        const threshold = parseFloat(thresholdInput.value);
        if (!Number.isFinite(threshold)) return false;

        const isHazmatInput = formElement.querySelector('input[name="hazmat-is-item"]');
        const isHazmatItem = String(isHazmatInput?.value || "").toLowerCase() === "true";
        if (!isHazmatItem) return false;

        const hazmatVolumeInput = formElement.querySelector('input[name="hazmat-volume"]');
        const itemHazmatVolume = parseFloat(hazmatVolumeInput?.value || "0");
        const quantityToAdd = Number(formData.get("quantity")) || 1;

        if (threshold === 0) {
          this.handleErrorMessage(window.variantStrings.hazmatRestrictedMessage);
          return true;
        }

        if (itemHazmatVolume >= 50) {
          this.handleErrorMessage(window.variantStrings.hazmatRestrictedMessage);
          return true;
        }

        // The saved volume describes the bag's lines, and the bag never writes it while empty. A country switch
        // that empties the bag leaves the old value behind, so an empty bag counts as no hazmat volume.
        const cartHasLines = Number(cart?.item_count) > 0 || (cart?.items?.length || 0) > 0;
        const prevHazmatVolume = cartHasLines ? Number(cart?.attributes?._prevHazmatVolume || 0) : 0;
        const currentHazmatVolume = Number.isFinite(prevHazmatVolume) ? prevHazmatVolume : 0;
        const projectedHazmatVolume = currentHazmatVolume + itemHazmatVolume * quantityToAdd;

        if (projectedHazmatVolume >= 500) {
          this.handleErrorMessage(window.variantStrings.hazmatRestrictedMessage);
          return true;
        }

        return false;
      }

      refreshSubmitButtonText() {
        if (!this.submitButton) return null;
        if (this.submitButtonText && this.submitButtonText?.isConnected) return this.submitButtonText;
        const span = this.submitButton.querySelector("span");
        if (span) {
          this.submitButtonText = span;
          return span;
        }
        this.submitButtonText = null;
        return null;
      }

      readSubmitButtonText() {
        if (!this.submitButton) return "";
        const labelEl = this.refreshSubmitButtonText();
        return labelEl ? labelEl.textContent : this.submitButton.textContent || "";
      }

      writeSubmitButtonText(value) {
        if (!this.submitButton) return;
        const labelEl = this.refreshSubmitButtonText();
        if (labelEl) {
          labelEl.textContent = value;
        } else {
          const span = document.createElement("span");
          span.textContent = value;
          this.submitButton.textContent = "";
          this.submitButton.appendChild(span);
          this.submitButtonText = span;
        }

        // Keep the accessible name in step with the visible text. hb-datalayer-events.js reads 'pre-order'
        // from this aria-label (forcePre), so a label that contains it is never written here.
        const suffix = this.submitButton.dataset.a11ySuffix;
        const text = String(value ?? "").trim();
        if (suffix != null && text && !text.toLowerCase().includes("pre-order")) {
          this.submitButton.setAttribute("aria-label", text + suffix);
        }
      }

      storeSubmitButtonDefaultLabel() {
        if (!this.submitButton) return;
        const label = this.readSubmitButtonText();
        this.submitButton.dataset.defaultLabel = label;
        this.submitButton.dataset.defaultLabelClean = label.trim().replace(/\u2026$|\.\.\.$/, "");
      }

      setSubmitButtonLoadingState(isLoading) {
        if (!this.submitButton) return;
        if (isLoading) {
          if (!this.submitLockActive) {
            this.storeSubmitButtonDefaultLabel();
            this.submitLockActive = true;
            window.__hbAtcLock?.increment?.();
          }
          const loadingText = window.variantStrings?.adding;
          this.writeSubmitButtonText(loadingText);
          this.submitButton.classList.add("product-form__submit--loading", "product-form__submit--locked");
        } else {
          if (this.submitLockActive) {
            this.submitLockActive = false;
            window.__hbAtcLock?.decrement?.();
          }
          const original = this.submitButton.dataset.defaultLabel;
          if (original) this.writeSubmitButtonText(original);
          this.submitButton.classList.remove("product-form__submit--loading", "product-form__submit--locked");
        }
      }

      disableATCButtons() {
        const clickedBtn = this.submitButton;
        const predictiveScope =
          clickedBtn?.closest?.(".predictive-search") || clickedBtn?.closest?.("predictive-search");

        this._atcScopeEl = predictiveScope || document;

        const btns = this._atcScopeEl.querySelectorAll("button.product-form__submit");
        btns.forEach((btn) => {
          if (!btn) return;
          if (predictiveScope && btn === clickedBtn) return;
          if (!btn.hasAttribute("disabled")) {
            btn.dataset.atcTempDisabled = "1";
            btn.setAttribute("disabled", "");
            btn.setAttribute("aria-disabled", "true");
            btn.classList.add("product-form__submit--locked");
          }
        });
      }

      /*
       * A button whose shade was picked unavailable meanwhile (setVariantAvailable) stays disabled. While
       * another hold keeps the global lock (on the cart page this add's own cart hold, until the gift
       * writer is idle), the lock owns the button: its state is handed to the unlock in
       * onGlobalAtcLockChange instead, so the button is not left tappable, or later stuck, mid-write.
       */
      enableATCButtons() {
        const scope = this._atcScopeEl || document;
        const lockHeld = Boolean(window.__hbAtcLock?.count);
        scope.querySelectorAll("button.product-form__submit[data-atc-temp-disabled]").forEach((btn) => {
          delete btn.dataset.atcTempDisabled;
          const unavailable = btn.dataset.variantUnavailable === "true";
          if (lockHeld && btn.dataset.atcGlobalLocked === "true") {
            btn.dataset.atcGlobalPrevDisabled = unavailable ? "true" : "false";
            btn.dataset.atcGlobalPrevAria = "false";
            // Styled as the lock styles the buttons it disables; a sold-out shade looks disabled as before.
            btn.classList.toggle("product-form__submit--locked", !unavailable);
            return;
          }
          if (!unavailable) btn.removeAttribute("disabled");
          btn.removeAttribute("aria-disabled");
          btn.classList.remove("product-form__submit--locked");
        });
        this._atcScopeEl = null;
      }

      setDkSilent(on) {
        window.__dkCartSilent = !!on;
      }

      async runDkSilent(cb) {
        this.setDkSilent(true);
        try {
          return await cb();
        } finally {
          setTimeout(() => this.setDkSilent(false), 80);
        }
      }

      getVariantQuantity(cart, variantId) {
        if (!variantId || !cart?.items?.length) return 0;
        const found = cart.items.find((item) => item?.variant_id == variantId);
        return found ? Number(found.quantity) || 0 : 0;
      }

      /*
       * Resolves with Shopify's answer (the cart lines, or its JSON error such as a 422), or with
       * { hbTransient } when the store could not answer the add: "rate-limited" (429, which Shopify did not
       * process), "server" (5xx, or an error page that is not JSON), "timeout" or "network" (the request was
       * sent but no answer came, so the add may have landed). A transient failure is never a sold out.
       */
      async parallelAddToCart(config) {
        const addUrl = `${routes.cart_add_url}?dk_silent=1`;
        const silentConfig = { ...config, headers: { ...config.headers, "X-DK-Silent": "1" } };
        let res;
        try {
          res = await this.fetchWithTimeout(addUrl, silentConfig, HB_ADD_TIMEOUT_MS);
        } catch (error) {
          const timedOut = error?.name === "TimeoutError" || error?.name === "AbortError";
          return { hbTransient: timedOut ? "timeout" : "network" };
        }
        if (res.status === 429) {
          return { hbTransient: "rate-limited", retryAfterMs: parseRetryAfterMs(res.headers.get("Retry-After")) };
        }
        if (res.status >= 500) return { hbTransient: "server" };
        const body = await res.json().catch(() => null);
        if (body && typeof body === "object") return body;
        // A 2xx or 4xx that is not JSON is a bot check or an error page, not Shopify's cart answer.
        return { hbTransient: res.ok ? "timeout" : "server" };
      }

      // One automatic retry, only for a 429 (Shopify did not process the add) and only for a short wait.
      async addToCartWithRetry(config) {
        const first = await this.parallelAddToCart(config);
        if (first?.hbTransient !== "rate-limited" || first.retryAfterMs > HB_ADD_RETRY_MAX_WAIT_MS) return first;
        await new Promise((resolve) => setTimeout(resolve, first.retryAfterMs));
        return this.parallelAddToCart(config);
      }

      /*
       * The add did not get an answer it could trust. After a timeout, a dropped connection or a non-JSON
       * reply it may still have landed, so the cart is read (GET) and compared with the snapshot taken before
       * the add: the cart when the variant's quantity went up, else null. A 429 was not processed, so nothing
       * is read.
       */
      async addLandedAfterTransientFailure(kind, cartSnapshot, variantId) {
        if (kind === "rate-limited" || !cartSnapshot || !variantId) return null;
        const cart = await this.fetchCart();
        if (!cart) return null;
        const landed = this.getVariantQuantity(cart, variantId) > this.getVariantQuantity(cartSnapshot, variantId);
        if (!landed) return null;
        try {
          window.hbRefreshCartBubble?.();
          window.hbCartDrawer?.refresh?.({ reason: "add-recovered" });
        } catch {}
        return cart;
      }

      // The button goes back to Add to bag, usable, with a short busy message instead of Sold out or a redirect.
      showTransientAddFailure() {
        const soldOutMessage = this.submitButton?.querySelector(".sold-out-message");
        soldOutMessage?.classList.add("hidden");
        this.refreshSubmitButtonText()?.classList.remove("hidden");
        this.error = false;
        this.handleErrorMessage(
          window.variantStrings?.addToBagBusy || "We're busy right now. Please try again in a moment.",
        );
      }

      buildCartItemFromFormData(formData) {
        const item = {
          id: formData.get("id"),
          quantity: Number(formData.get("quantity")) || 1,
        };

        const properties = {};

        formData.forEach((value, key) => {
          if (key.startsWith("properties[")) {
            const propName = key.slice("properties[".length, -1);
            properties[propName] = value;
          }
        });
        if (Object.keys(properties).length) {
          item.properties = properties;
        }

        return item;
      }

      syncSuggestedCardVariantInput() {
        const card = this.closest(".suggested-items__product-card__wrapper");
        if (!card || card.classList.contains("bundled-suggested-item")) return;

        const selectedOption = card.querySelector(".custom-option.selected[data-id]");
        if (!selectedOption?.dataset?.id || !this.variantIdInput) return;

        this.variantIdInput.value = selectedOption.dataset.id;
        this.variantIdInput.disabled = false;
      }

      /*
       * On the cart page every cart write (a promo or loyalty code, a line change, the gift checks, an add
       * once it runs) holds window.hbCartBusy (assets/cart.js), which also holds the global add-to-cart lock.
       * Promo and loyalty writes do not run through window.__cartQueue, so an add let through while one
       * settles raced it and could add a gift against a threshold that was still changing: a shade pick
       * could re-enable a button the lock had left alone. The add is refused while any hold but this
       * form's own is open (its own lasts until the gift writer is idle after its add). Another add still
       * waiting in window.__cartQueue holds nothing yet, so this one queues behind it as before. The same
       * registry serves the cart drawer wherever it is open (assets/cart.js loads with it, and its
       * <cart-items> is what this checks for); pages with neither have no hbCartBusy and are unchanged.
       */
      isBlockedByCartWrite() {
        const cartBusy = window.hbCartBusy;
        if (!cartBusy || !document.querySelector("cart-items")) {
          return false;
        }
        const holds = typeof cartBusy.holds === "function" ? cartBusy.holds().length : Number(Boolean(cartBusy.isBusy?.()));
        return holds > (this.cartBusyHold ? 1 : 0);
      }

      /*
       * The checks onSubmitHandler refuses an add on before anything else runs. assets/hb-datalayer-events.js
       * reads them on the button's click, which fires before the submit, so a refused add is not reported to
       * GTM as an add_to_cart.
       */
      isSubmitRefused() {
        return this.submitButton?.getAttribute("aria-disabled") === "true" || this.isBlockedByCartWrite();
      }

      // The variant an add from this form sends. A suggested card's picked option is copied into the id
      // input only on submit (syncSuggestedCardVariantInput), after the click hb-datalayer-events.js reads.
      getSubmitVariantId() {
        const card = this.closest(".suggested-items__product-card__wrapper");
        if (card && !card.classList.contains("bundled-suggested-item")) {
          const selectedId = card.querySelector(".custom-option.selected[data-id]")?.dataset?.id;
          if (selectedId) return String(selectedId);
        }
        return String(this.variantIdInput?.value || "");
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;
        if (this.isBlockedByCartWrite()) {
          // Only reachable in a race, since the cart write's hold disables the buttons through the add to
          // cart lock. Say why nothing happened, as the promo field does, rather than read as a dead button.
          this.handleErrorMessage(
            document.querySelector("[data-cart-promo-form]")?.dataset.busyMessage || window.cartStrings?.processing,
          );
          return;
        }

        if (this.cart?.tagName === "CART-NOTIFICATION" && typeof this.cart.close === "function") {
          this.cart.close();
        }

        // An add from the gift and sample picker keeps keyboard focus on the card. Disabling the buttons below drops
        // it to <body> and the render replaces the card, so the position is taken now and handed back in the finally
        // (the picker finds the re-rendered card by its form id; its button is now Remove). Null for any other form.
        const picker = this.closest("cart-page-samples-gifts-picker");
        const pickerFocusReturn = picker?.getKeyboardFocusReturn?.(this.submitButton) || null;
        const recommendations = this.closest("hb-cart-cross-sell, hb-cart-wishlist");
        const recommendationFocusReturn = recommendations?.getKeyboardFocusReturn?.(this.submitButton) || null;

        this.submitButton.setAttribute("aria-disabled", "true");
        this.disableATCButtons();
        this.setSubmitButtonLoadingState(true);

        await window.__cartQueue.enqueue(async () => {
        this.toggleCartOverlay(true);
        try {
        this.handleErrorMessage();
        this.syncSuggestedCardVariantInput();

        const formData = new FormData(this.form);
        const addedVariantId = formData.get("id");

        const cartRemoveItems = document.querySelectorAll("cart-remove-button");
        cartRemoveItems.forEach((i) => (i.style.pointerEvents = "none"));

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";

        // The drawer names the sections it renders (the cart template's ids, which only it knows). One that
        // exists but has not upgraded yet only gets the bubble; the add then ends on the cart page, below.
        const cart = this.cart;
        const cartIsUpgraded = typeof cart?.getSectionsToRender === "function";
        const sectionsToRender = cartIsUpgraded
          ? cart.getSectionsToRender().map((s) => s.section || s.id)
          : cart
            ? ["cart-icon-bubble"]
            : [];
        // Sections are rendered as the cart URL's page when the drawer asks for it (routes.cart_url), so
        // they carry the cart template's context; the notification keeps rendering as the current page.
        const sectionsUrl =
          typeof cart?.getSectionsUrl === "function" ? cart.getSectionsUrl() : window.location.pathname;

        const cartPromise = this.fetchCart();

        let cartSnapshot = null;
        let cartStateForUi = null;
        try {
          cartSnapshot = await cartPromise;
          cartStateForUi = cartSnapshot;
          const offlineRemoval = await removeOfflineCartItems();
          if (offlineRemoval.removed) {
            cartSnapshot = await this.fetchCart();
            cartStateForUi = cartSnapshot;
          }

          const hasValidationError = this.validateMaxQuantityWithCartSnapshot(formData, evt.target, cartSnapshot);
          if (hasValidationError) {
            this.setSubmitButtonLoadingState(false);
            this.submitButton.removeAttribute("aria-disabled");
            this.enableATCButtons();
            this.toggleCartOverlay(false);
            cartRemoveItems.forEach((i) => (i.style.pointerEvents = "auto"));
            return;
          }

          const hasHazmatValidationError = this.validateHazmatRules(formData, evt.target, cartSnapshot);
          if (hasHazmatValidationError) {
            this.setSubmitButtonLoadingState(false);
            this.submitButton.removeAttribute("aria-disabled");
            this.enableATCButtons();
            this.toggleCartOverlay(false);
            cartRemoveItems.forEach((i) => (i.style.pointerEvents = "auto"));
            return;
          }
        } catch (e) {
          // if cart snapshot fails, continue and let server guard quantities
          console.warn("Cart snapshot failed, proceeding", e);
        }

        // Last word before the cart write, after the queue and the cart read: a caller can withdraw the add (the
        // wishlist page, when another account took over this browser meanwhile). Sent on this element, which a
        // re-render may have detached; nothing listens on other forms, so they always go on.
        if (!this.dispatchEvent(new CustomEvent("hb:product-form:before-add", { cancelable: true, detail: { variantId: addedVariantId } }))) {
          this.setSubmitButtonLoadingState(false);
          this.submitButton.removeAttribute("aria-disabled");
          this.enableATCButtons();
          this.toggleCartOverlay(false);
          cartRemoveItems.forEach((i) => (i.style.pointerEvents = "auto"));
          return;
        }

        const mainItem = this.buildCartItemFromFormData(formData);
        const itemsPayload = [mainItem];

        config.body = JSON.stringify({
          items: itemsPayload,
          sections: sectionsToRender,
          sections_url: sectionsUrl,
        });

        let response;
        this.error = false;
        try {
          response = await this.runDkSilent(() => this.addToCartWithRetry(config));
        } catch (e) {
          console.error(e);
          response = { hbTransient: "network" };
        }

        // Rate limited, a server error or no answer: never a sold out and never a redirect. The add is not
        // sent again here (a timeout may have landed); the cart is read instead, and an add that landed is
        // shown in the drawer as any add is.
        if (response?.hbTransient) {
          const transientKind = response.hbTransient;
          console.warn(`Add to bag not confirmed (${transientKind})`);
          let landedCart = null;
          try {
            landedCart = await this.addLandedAfterTransientFailure(transientKind, cartSnapshot, addedVariantId);
          } catch {}
          if (landedCart) {
            try {
              document.dispatchEvent(
                new CustomEvent("hb:product-form:added", {
                  detail: { submitEvent: evt, productForm: this, variantId: addedVariantId },
                }),
              );
              // The PDP's quantity stepper takes over from Add to bag, as after an answered add.
              if (evt.target.querySelector("pdp-inline-quantity")) {
                const line = landedCart.items?.find((item) => String(item?.variant_id) === String(addedVariantId));
                document.dispatchEvent(
                  new CustomEvent("pdp-inline-quantity:sync", {
                    detail: { variantId: addedVariantId, quantity: Number(line?.quantity) || 1, key: line?.key || "" },
                  }),
                );
              }
            } catch {}
            if (this.cart?.tagName === "CART-DRAWER" && !this.closest("#CartDrawer")) {
              window.hbCartDrawer?.open?.({ reason: "api", opener: this.submitButton });
            }
          } else {
            this.showTransientAddFailure();
          }
          await this.finishSubmit(cartRemoveItems);
          return;
        }

        // Read before a 422's cart refresh below replaces response: that add was refused even if the cart moved.
        const addAccepted = Boolean(response) && typeof response === "object" && !response.status;
        let handled422 = false;

        if (response?.status === 422) {
          const availabilityMessage = response?.description || response?.message || "";
          try {
            const updatedState = await updateCartUI(false, cartSnapshot);
            if (updatedState) {
              response = updatedState;
              const cartChanged = hasCartChanged(cartSnapshot, updatedState);
              if (cartChanged) handled422 = true;
              cartStateForUi = updatedState || cartSnapshot;
            }
          } catch (err) {
            console.warn("Silent cart refresh after 422 failed:", err);
          }

          if (availabilityMessage) {
            this.handleErrorMessage(availabilityMessage);
          }
        }

        // handle errors fast
        if (response?.status && !handled422) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: "product-form",
            productVariantId: formData.get("id"),
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          if (response?.status == 422 && response?.message?.includes(window.variantStrings.hazmatRestrictedMessage)) {
            this.handleErrorMessage(response.message);
            updateCartUI();
          } else if (response?.status == 422) {
            updateCartUI();
          } else {
            const soldOutMessage = this.submitButton.querySelector(".sold-out-message");
            if (soldOutMessage) {
              this.submitButton.setAttribute("aria-disabled", true);
              const labelEl = this.refreshSubmitButtonText();
              labelEl?.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
            }
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
          }

          // finish
          await this.finishSubmit(cartRemoveItems);
          return;
        }

        // assets/hb-datalayer-events.js pushes add_to_cart from this, so only an add /cart/add accepted is reported.
        // Sent on document: a re-render while the add was in flight can have detached this form.
        if (addAccepted) {
          try {
            document.dispatchEvent(
              new CustomEvent("hb:product-form:added", {
                detail: { submitEvent: evt, productForm: this, variantId: addedVariantId },
              }),
            );
          } catch {}
        }

        // update PDP quantity roles quickly
        const productWrapper = evt.target.closest(".product-page");
        try {
          const quantityRolesData =
            cartStateForUi && addedVariantId
              ? { quantity: this.getVariantQuantity(cartStateForUi, addedVariantId), variant_id: addedVariantId }
              : response;
          updateQuantityRolesPDP(productWrapper, quantityRolesData);
        } catch {}

        // render the main add result immediately
        const cartChangedSinceSnapshot = cartSnapshot && response ? hasCartChanged(cartSnapshot, response) : true;
        // ensure the cart-notification heading shows the default "Item added" string
        window.__pdpInlineHeadingOverride = null;
        // True when the drawer renders this add itself, so the cartUpdate below does not read as an outside change.
        let drawerRendersAdd = false;
        if (!this.error && cartChangedSinceSnapshot && !handled422) {
          drawerRendersAdd = cartIsUpgraded && cart.tagName === "CART-DRAWER";
          const quickAddModal = this.closest("quick-add-modal");
          // Read before the modal closes: hide(true) empties it (this form goes with it) and clears openedBy.
          // The drawer hands focus back to the opener when it closes.
          const opener = quickAddModal?.openedBy || this.submitButton;
          if (quickAddModal) {
            document.body.addEventListener(
              "modalClosed",
              () => {
                queueMicrotask(() => this.showCartAfterAdd(response, opener, cartIsUpgraded));
              },
              { once: true },
            );
            quickAddModal.hide(true);
          } else {
            this.showCartAfterAdd(response, opener, cartIsUpgraded);
          }
        }

        // notify the PDP inline quantity selector (only present on the main PDP ATC)
        try {
          const inlineQty = evt.target.querySelector("pdp-inline-quantity");
          if (inlineQty) {
            const addedLine = Array.isArray(response?.items)
              ? response.items.find((i) => String(i?.variant_id) === String(addedVariantId)) || response.items[0]
              : response;

            const detail = { variantId: addedVariantId };
            if (addedLine && typeof addedLine.quantity === "number") {
              detail.quantity = addedLine.quantity;
              detail.key = addedLine.key || "";
            } else {
              const prevQty =
                cartStateForUi && typeof this.getVariantQuantity === "function"
                  ? this.getVariantQuantity(cartStateForUi, addedVariantId)
                  : 0;
              detail.quantity = prevQty + 1;
            }

            document.dispatchEvent(
              new CustomEvent("pdp-inline-quantity:sync", { detail }),
            );
          }
        } catch {}

        // post publish without blocking
        try {
          const publishCartData = response?.sections ? response : cartStateForUi || response;
          publish?.(PUB_SUB_EVENTS?.cartUpdate, {
            source: "product-form",
            productVariantId: formData.get("id"),
            cartData: publishCartData,
            ...(drawerRendersAdd ? { surfaceRendered: true } : {}),
          });

          document.dispatchEvent(
            new CustomEvent("cart:predictive-add", {
              detail: { variantId: formData.get("id"), source: "predictive-search" },
            }),
          );

          queueMicrotask(() => {
            try {
              window.DiscountKit?.refresh?.();
              window.discountKit?.refresh?.();
              publish?.(PUB_SUB_EVENTS?.cartUpdate, {
                source: "product-form-post-silent",
                ...(drawerRendersAdd ? { surfaceRendered: true } : {}),
              });
            } catch {}
          });
        } catch {}

        // finish
        await this.finishSubmit(cartRemoveItems);
        } finally {
          // finishSubmit() and the validation returns above already released the cart-page hold, so this
          // is a no-op unless something threw first; checkout must never stay locked after a failed add.
          this.toggleCartOverlay(false);
          try {
            picker?.returnKeyboardFocus?.(pickerFocusReturn);
            recommendations?.returnKeyboardFocus?.(recommendationFocusReturn);
          } catch {}
        }
        });
      }

      /*
       * Shows the notification, or opens the drawer, for page adds. A form inside the drawer only updates it
       * (renderContents ignores open for an origin inside the drawer; the notification ignores the options).
       * A surface that had not upgraded when the add was sent asked for the bubble only, so its
       * response holds nothing to render; with no surface at all there is nothing to show it on. Either way
       * the shopper goes to the cart page, which has everything the drawer would have.
       */
      showCartAfterAdd(response, opener, surfaceWasReady) {
        const cart = this.cart;
        if (surfaceWasReady && typeof cart?.renderContents === "function") {
          cart.renderContents(response, { open: true, origin: this, opener });
          if (cart.tagName === "CART-DRAWER" && !this.closest("#CartDrawer")) this.returnFocusAfterDrawer(opener);
          return;
        }
        window.location.assign(window.routes.cart_url);
      }

      /*
       * The drawer hands focus back to the opener when it closes, or to the bag icon at the top of the page when
       * the opener is hidden. On a PDP the add hides Add to Bag and shows the quantity stepper in its place, so
       * focus goes to the stepper's + button instead: where the shopper was, and it cannot remove the item.
       * One pending return at a time (the latest add wins).
       */
      returnFocusAfterDrawer(opener) {
        if (window.__hbDrawerFocusReturn) {
          document.removeEventListener("hb:cart-drawer:closed", window.__hbDrawerFocusReturn);
        }
        const onClosed = () => {
          window.__hbDrawerFocusReturn = null;
          const active = document.activeElement;
          if (opener && active === opener) return;
          // Only replace the drawer's fallback (the bag icon, or no focus at all), never a choice it made.
          if (active && active !== document.body && active.id !== "cart-icon-bubble") return;
          const stepper = this.querySelector("pdp-inline-quantity:not([hidden])");
          const target =
            stepper?.querySelector(".pdp-inline-quantity__btn--plus:not([disabled])") ||
            stepper?.querySelector("button:not([disabled])");
          if (!target || target.closest("[inert]") || !target.getClientRects().length) return;
          target.focus({ preventScroll: true });
        };
        window.__hbDrawerFocusReturn = onClosed;
        document.addEventListener("hb:cart-drawer:closed", onClosed, { once: true });
      }

      async finishSubmit(cartRemoveItems) {
        this.setSubmitButtonLoadingState(false);
        this.enableATCButtons();
        if (this.cart && this.cart.classList.contains("is-empty")) this.cart.classList.remove("is-empty");
        if (!this.error) this.submitButton.removeAttribute("aria-disabled");
        cartRemoveItems.forEach((i) => (i.style.pointerEvents = "auto"));
        try {
          await window.waitForGiftCartIfBusy?.();
        } finally {
          this.toggleCartOverlay(false);
        }
      }

      handleErrorMessage(errorMessage = false) {
        const productForm = this;
        const formId = productForm.dataset.formId;
        const dlProductId = productForm.getAttribute("dl-product-id");
        const quickAddForm = this.closest(".product-actions__wrapper.quick-add-only");

        if (this.hideErrors) return;

        const isMainProduct = this.closest(".main-product");
        const isFeaturedCard = this.closest(".feature-card_product_buy-button-container");
        const isCartCrossSellCard = this.closest(".hb-cart-cross-sell__card");
        const isCartPagePickerCard = this.closest(".cart-page-picker__product-card");
        const isstickyAddToCart = this.closest(".add-to-cart-panel");

        if (isMainProduct) {
          const isQuickAdd = isMainProduct.querySelector(".quick-add-to-bag");
          if (isQuickAdd) {
            this.errorMessageWrapper = quickAddForm?.querySelector(
              `.product-form__error-message-wrapper[data-form-id="${formId}"]`,
            );
          } else {
            this.errorMessageWrapper = isMainProduct.querySelector(".product-form__error-message-wrapper.pdp");
          }
        } else if (isFeaturedCard) {
          this.errorMessageWrapper = productForm
            .closest(".feature-card_product_buy-button-container")
            ?.querySelector(`.product-form__error-message-wrapper[dl-product-id="${dlProductId}"]`);
        } else if (isCartCrossSellCard) {
          this.errorMessageWrapper = isCartCrossSellCard.querySelector("[data-cross-sell-atc-error]");
        }
        if (isstickyAddToCart) {
          this.errorMessageWrapper =
            this.errorMessageWrapper ||
            document.querySelector('.product-form__error-message-wrapper[data-form-id="sticky-add-to-cart-form"]');
        } else {
          this.errorMessageWrapper =
            this.errorMessageWrapper ||
            document.querySelector(`.product-form__error-message-wrapper[data-form-id="${formId}"]`);
        }

        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector(".product-form__error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (this._errorMessageTimeout) {
          clearTimeout(this._errorMessageTimeout);
          this._errorMessageTimeout = null;
        }

        if (this.errorMessageWrapper) {
          this.errorMessageWrapper.classList.remove("fade-out");
        }

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          if (this.errorMessageWrapper.classList.contains("pdp")) {
            this.errorMessageWrapper.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          // A shipping restriction stays until the next add or redraw: faded after 3s, it was gone before it could be
          // read and the button invited the same failing click again.
          const isHazmatError = errorMessage === window.variantStrings?.hazmatRestrictedMessage;
          if ((isFeaturedCard || isCartPagePickerCard) && !isHazmatError) {
            this._errorMessageTimeout = setTimeout(() => {
              if (this.errorMessageWrapper) {
                this.errorMessageWrapper.classList.add("fade-out");
                setTimeout(() => {
                  if (this.errorMessageWrapper) {
                    this.errorMessageWrapper.toggleAttribute("hidden", true);
                    this.errorMessageWrapper.classList.remove("fade-out");
                  }
                }, 300);
              }
              this._errorMessageTimeout = null;
            }, 3000);
          }
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (this.submitButton?.hasAttribute("preorder")) {
            this.writeSubmitButtonText((window.variantStrings?.preorder || "Pre-Order"));
          }
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButton.removeAttribute("aria-disabled");
          this.writeSubmitButtonText(window.variantStrings.addToCart);
        }

        if (this.submitButton?.hasAttribute("preorder") && disable) {
          this.writeSubmitButtonText((window.variantStrings?.preorderOutOfStock || "Out Of Stock"));
        } else if (text) {
          this.writeSubmitButtonText(text);
        } else {
          this.writeSubmitButtonText(window.variantStrings.addToCart);
        }

        this.storeSubmitButtonDefaultLabel();
      }

      toggleCartOverlay(isLoading) {
        const cartItems = document.querySelector("cart-items");
        const isCartPage = window.location.pathname.includes("/cart");
        const cartBusy = window.hbCartBusy;

        // Wherever the cart is on screen (the cart page, or the drawer once its cart script has loaded) an
        // add holds the shared busy registry (assets/cart.js) instead of a page overlay: checkout and the
        // other cart controls stay disabled until it settles. The hold lives on this form and is released at
        // most once, so the extra release in onSubmitHandler's finally (and any unmatched call) can never
        // free another writer's hold.
        if (!isLoading && this.cartBusyHold) {
          const hold = this.cartBusyHold;
          this.cartBusyHold = null;
          cartBusy?.release(hold);
          return;
        }
        if (cartItems && cartBusy) {
          if (isLoading) this.cartBusyHold ||= cartBusy.acquire("product-form");
          return;
        }

        if (isCartPage && cartItems && typeof cartItems.toggleOverlay === "function") {
          if (Boolean(this.cartOverlayOn) === Boolean(isLoading)) return;
          this.cartOverlayOn = Boolean(isLoading);
          cartItems.toggleOverlay(isLoading);
        }
      }

      get variantIdInput() {
        return this.form?.querySelector("[name=id]");
      }

      connectedCallback() {
        // Drawer panels move their live cards between containers. A move disconnects the element,
        // so restore the same submit handler instead of letting the form navigate to /cart/add.
        if (this._boundSubmit) this.form?.addEventListener("submit", this._boundSubmit, { passive: false });
        if (!this.hasGlobalLockListener) {
          document.addEventListener("hb:atc-lock-change", this.onGlobalAtcLockChange);
          this.hasGlobalLockListener = true;
        }
        this.onGlobalAtcLockChange();
      }

      disconnectedCallback() {
        if (this.hasGlobalLockListener) {
          document.removeEventListener("hb:atc-lock-change", this.onGlobalAtcLockChange);
          this.hasGlobalLockListener = false;
        }
        if (this._boundSubmit) {
          this.form?.removeEventListener("submit", this._boundSubmit);
        }
      }

      onGlobalAtcLockChange(event) {
        if (!this.submitButton) return;
        const locked =
          typeof event?.detail?.locked === "boolean" ? event.detail.locked : Boolean(window.__hbAtcLock?.count);
        const alreadyDisabled =
          this.submitButton.hasAttribute("disabled") || this.submitButton.getAttribute("aria-disabled") === "true";

        if (locked) {
          if (!this.submitButton.dataset.atcGlobalLocked) {
            this.submitButton.dataset.atcGlobalLocked = "true";
            this.submitButton.dataset.atcGlobalPrevDisabled = alreadyDisabled ? "true" : "false";
            this.submitButton.dataset.atcGlobalPrevAria =
              this.submitButton.getAttribute("aria-disabled") === "true" ? "true" : "false";
          }
          if (!this.submitButton.dataset.defaultLabel) {
            this.storeSubmitButtonDefaultLabel();
          }
          // A button that was already disabled (an unavailable shade) is marked too: onSubmitHandler reads
          // aria-disabled, and a shade change must not be able to clear the lock with `disabled` alone.
          if (alreadyDisabled) this.submitButton.setAttribute("aria-disabled", "true");
          if (!alreadyDisabled) {
            this.submitButton.setAttribute("disabled", "");
            this.submitButton.setAttribute("aria-disabled", "true");
            this.submitButton.classList.add("product-form__submit--locked");
            if (this.submitLockActive) {
              this.submitButton.classList.add("product-form__submit--loading");
              const loadingText = window.variantStrings?.adding;
              this.writeSubmitButtonText(loadingText);
            } else {
              this.submitButton.classList.remove("product-form__submit--loading");
              const defaultLabel = this.submitButton.dataset.defaultLabel;
              if (defaultLabel) this.writeSubmitButtonText(defaultLabel);
            }
          }
        } else if (this.submitButton.dataset.atcGlobalLocked === "true") {
          const wasDisabled = this.submitButton.dataset.atcGlobalPrevDisabled === "true";
          const hadAriaDisabled = this.submitButton.dataset.atcGlobalPrevAria === "true";
          delete this.submitButton.dataset.atcGlobalLocked;
          delete this.submitButton.dataset.atcGlobalPrevDisabled;
          delete this.submitButton.dataset.atcGlobalPrevAria;

          this.submitButton.classList.remove("product-form__submit--locked");
          if (!this.submitLockActive) {
            this.submitButton.classList.remove("product-form__submit--loading");
          }

          const ownedElsewhere = this.submitLockActive || this.submitButton.dataset.atcTempDisabled;
          if (!wasDisabled && !ownedElsewhere) {
            this.submitButton.removeAttribute("disabled");
            this.submitButton.removeAttribute("aria-disabled");
            const original = this.submitButton.dataset.defaultLabel;
            this.writeSubmitButtonText(original || this.readSubmitButtonText());
            this.storeSubmitButtonDefaultLabel();
          } else if (wasDisabled && !hadAriaDisabled && !ownedElsewhere) {
            // Only the lock set aria-disabled on this already disabled button; `disabled` stays as it was.
            this.submitButton.removeAttribute("aria-disabled");
          }
        }
      }

      /*
       * Shade pickers (assets/suggested-items.js) set the button from the picked variant's availability
       * through this. While the global add-to-cart lock is held the lock owns the button, so the state is
       * recorded for the unlock above to apply instead: a pick of an available shade mid-write must not
       * enable Add, and one of an unavailable shade must leave it disabled once the lock lifts.
       */
      setVariantAvailable(available) {
        if (!this.submitButton) return;
        const button = this.submitButton;
        // Read by enableATCButtons, which would otherwise lift another add's temporary disable regardless.
        if (available) {
          delete button.dataset.variantUnavailable;
        } else {
          button.dataset.variantUnavailable = "true";
        }
        if (button.dataset.atcGlobalLocked === "true") {
          // Disabling is always safe mid-lock; enabling waits for the unlock. A sold-out shade looks sold out
          // now, as the lock leaves a button that was already disabled.
          if (!available) {
            button.disabled = true;
            button.classList.remove("product-form__submit--locked");
          }
          const staysDisabled = !available || button.dataset.atcGlobalPrevAria === "true";
          button.dataset.atcGlobalPrevDisabled = staysDisabled ? "true" : "false";
          return;
        }
        button.disabled = !available;
      }
    },
  );
}
